import { useThreadRuntime } from "@assistant-ui/react";
import { ChevronRight } from "lucide-react";
import { Item, ItemContent, ItemDescription, ItemTitle } from "@repo/ui/components/ui/item";
import { AskTigerLogo } from "./AskTigerLogo";
import { AskTigerSparkle } from "./AskTigerSparkle";
import { welcomeScreenStyles as s } from "./WelcomeScreen.styles";
import type { WelcomeScreenProps } from "./WelcomeScreen.types";
import type { CategoryId } from "./CategoryView.types";
import type { CapabilityCard } from "./capabilities";

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/** Card shape the welcome screen renders; static and registry-driven cards both normalize to this. */
interface WelcomeActionCard {
  id: string;
  label: string;
  description: string;
  category?: CategoryId;
  prompt: string;
}

/** Static fallback, shown only after the capabilities bootstrap failed. */
const FALLBACK_CARDS: WelcomeActionCard[] = [
  {
    id: "summarize",
    label: "Summarize conversation",
    description: "AI summary of current thread",
    prompt: "Summarize this conversation",
  },
  {
    id: "oncall",
    label: "On-Call Lookup",
    description: "Find who's on call",
    category: "oncall",
    prompt: "Who is on call right now?",
  },
  {
    id: "support",
    label: "Customer Support",
    description: "Get help from support",
    category: "support",
    prompt: "I need help from customer support",
  },
];

/**
 * Sub-agents with a dedicated category view; their cards navigate there instead of
 * sending the prompt directly. Keyed by registry name (`CapabilityCard.agent`).
 */
const CATEGORY_BY_AGENT: Partial<Record<string, CategoryId>> = {
  scheduling: "oncall",
};

function toActionCard(card: CapabilityCard): WelcomeActionCard {
  return {
    id: card.id,
    label: card.label,
    description: card.description,
    category: CATEGORY_BY_AGENT[card.agent ?? card.id],
    prompt: card.prompt ?? card.examples?.[0] ?? card.label,
  };
}

const SKELETON_CARD_COUNT = 4;

export function WelcomeScreen({
  userName,
  onSelectCategory,
  agentCards,
  agentCardsLoading,
  agentCardsError,
}: WelcomeScreenProps) {
  const threadRuntime = useThreadRuntime();

  // Every card is registry-driven: skeletons hold their place while the bootstrap is in
  // flight, and the static fallback is reserved for a bootstrap that failed or delivered
  // nothing — never for the in-flight window, which would flash cards the agent replaces.
  const showSkeletons = Boolean(agentCardsLoading) && !agentCards?.length;
  const showFallback = !showSkeletons && (Boolean(agentCardsError) || !agentCards?.length);
  const cards = showSkeletons
    ? []
    : showFallback
      ? FALLBACK_CARDS
      : (agentCards ?? []).map(toActionCard);

  const handleCardClick = (card: WelcomeActionCard) => {
    if (card.category && onSelectCategory) {
      onSelectCategory(card.category);
    } else {
      threadRuntime.append(card.prompt);
    }
  };

  return (
    <div className={s.root}>
      <AskTigerLogo className={s.avatar} />
      <h2 className={s.greeting}>
        {getGreeting()}
        {userName ? `, ${userName}` : ""}
      </h2>
      <p className={s.subtitle}>How can I help you today?</p>

      <div className={s.cardList}>
        {cards.map((card) => (
          <Item key={card.id} asChild variant="outline" size="sm" className={s.card}>
            <button onClick={() => handleCardClick(card)} className={s.cardBtn}>
              <span className={s.cardIcon}>
                <AskTigerSparkle size={20} />
              </span>
              <ItemContent className={s.cardContent}>
                <ItemTitle className={s.cardTitle}>{card.label}</ItemTitle>
                <ItemDescription className={s.cardDesc}>{card.description}</ItemDescription>
              </ItemContent>
              <ChevronRight className={s.cardChevron} />
            </button>
          </Item>
        ))}
        {showSkeletons &&
          Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
            <div key={`skeleton-${i}`} className={s.skeletonCard} aria-hidden="true">
              <span className={s.skeletonIcon} />
              <span className={s.skeletonLines}>
                <span className={s.skeletonTitle} />
                <span className={s.skeletonDesc} />
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}
